import { zodResolver } from '@hookform/resolvers/zod';
import {
  type AdminSelf,
  type ApiResult,
  type CommandIntent,
  createIntent,
  type FieldError,
  isApiError,
  UnknownResultError,
} from '@saay/api-client';
import {
  formatRatePercent,
  MOBILE_E164_PATTERN,
  normalizeCodeInput,
  normalizeMobileField,
  normalizeShebaInput,
  statusLabel,
} from '@saay/formatting';
import {
  applyFieldErrors,
  Banner,
  Button,
  Checkbox,
  Dialog,
  ErrorState,
  FieldErrorList,
  KeyValueList,
  Ltr,
  Money,
  MoneyField,
  PercentField,
  SegmentedButtons,
  TextArea,
  TextField,
  toFormPath,
  useToast,
  WizardStepper,
} from '../kit.js';
import { useApiClient } from '@saay/web-app';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  Controller,
  type UseFieldArrayReturn,
  useFieldArray,
  useForm,
  type UseFormReturn,
} from 'react-hook-form';
import { Link, useNavigate, useOutletContext } from 'react-router';
import { z } from 'zod';

import type {
  AdminMerchant,
  InstallmentBoundsPolicyVersion,
  MerchantBranch,
  MerchantBranchCreate,
  MerchantCreate,
  MerchantUserCreate,
  MerchantUserCreated,
} from '../api.js';
import { useSubmitBlocked } from '../components/form.js';
import { usePageTitle } from '../components/guard.js';
import { onSheetOpenChange, useCloseSheet } from '../components/route-sheet.js';
import { MobileField, NationalCodeField, ShebaField } from '../components/masked-fields.js';
import { CategoryPicker, GeographyFields } from '../components/pickers.js';
import { canPerform, canUse } from '../permissions.js';
import { setupKeys, useCurrentPolicy } from '../queries.js';
import {
  type PlanIntervalBounds,
  PlanChoiceField,
  PlanIntervalField,
  planLabel,
} from './merchant-dialogs.js';
import {
  emptyMarketingSetup,
  marketingSetupError,
  MerchantMarketingSetupFields,
  toMerchantMarketingSetup,
  type MarketingSetupDraft,
} from './merchant-marketing-setup.js';
import {
  EMPTY_SETUP,
  hasSetupErrors,
  IDLE_SETUP,
  isSetupFilled,
  isSetupLocked,
  isSetupSettled,
  type MerchantCreatedState,
  runSetup,
  SETUP_LABELS,
  SETUP_ORDER,
  type SetupFieldErrors,
  type SetupItem,
  type SetupKey,
  type SetupProgress,
  type SetupValues,
  setupErrorMessage,
  validateSetup,
  wantedSetup,
} from './merchant-setup.js';
import { tehranToday } from '../validation.js';
import type { MerchantsOutletContext } from './merchants-list.js';

const text = (max: number) =>
  z.string().trim().min(1, 'این مورد الزامی است.').max(max, `حداکثر ${max} کاراکتر مجاز است.`);

const mobile = z
  .string()
  .transform(normalizeMobileField)
  .refine(
    (value) => MOBILE_E164_PATTERN.test(value),
    'شماره موبایل را مانند ۰۹۱۲۱۲۳۴۵۶۷ وارد کنید.',
  );

const required = z.string().min(1, 'این مورد الزامی است.');

const branchSchema = z.object({
  addressFa: text(500),
  cityId: required,
  nameFa: text(100),
  provinceId: required,
});

/** Mirrors the AC §17.5.2 bounds and legal-entity rules; the server decides every rule again. */
export const merchantWizardSchema = z.object({
  admin: z.object({ firstNameFa: text(100), lastNameFa: text(100), mobileE164: mobile }),
  branches: z.array(branchSchema).min(1, 'دست‌کم یک شعبه لازم است.').max(20),
  merchant: z
    .object({
      addressFa: text(500),
      businessLicenseNumber: z.string().trim().max(64),
      cityId: required,
      contactMobileE164: mobile,
      displayNameFa: text(200),
      legalEntityType: z.enum(['INDIVIDUAL', 'LEGAL_ENTITY']),
      legalNameFa: text(300),
      nationalId: z.string().transform(normalizeCodeInput),
      ownerNationalCode: z.string().transform(normalizeCodeInput),
      primaryCategoryId: required,
      provinceId: required,
      registrationNumber: z.string().trim().max(64),
    })
    .superRefine((value, context) => {
      const issue = (path: string, message: string) =>
        context.addIssue({ code: 'custom', message, path: [path] });
      if (value.legalEntityType === 'INDIVIDUAL') {
        if (!/^\d{10}$/.test(value.ownerNationalCode)) {
          issue('ownerNationalCode', 'کد ملی ۱۰ رقم است.');
        }
        if (value.businessLicenseNumber === '') {
          issue('businessLicenseNumber', 'برای شخص حقیقی شماره جواز کسب الزامی است.');
        }
      } else {
        if (!/^\d{11}$/.test(value.nationalId)) {
          issue('nationalId', 'شناسه ملی ۱۱ رقم است.');
        }
        if (value.registrationNumber === '') {
          issue('registrationNumber', 'برای شخص حقوقی شماره ثبت الزامی است.');
        }
      }
    }),
});

type WizardInput = z.input<typeof merchantWizardSchema>;
type WizardValues = z.output<typeof merchantWizardSchema>;

const emptyBranch = { addressFa: '', cityId: '', nameFa: '', provinceId: '' };

const defaults: WizardInput = {
  admin: { firstNameFa: '', lastNameFa: '', mobileE164: '' },
  branches: [{ ...emptyBranch, nameFa: 'شعبه مرکزی' }],
  merchant: {
    addressFa: '',
    businessLicenseNumber: '',
    cityId: '',
    contactMobileE164: '',
    displayNameFa: '',
    legalEntityType: 'INDIVIDUAL',
    legalNameFa: '',
    nationalId: '',
    ownerNationalCode: '',
    primaryCategoryId: '',
    provinceId: '',
    registrationNumber: '',
  },
};

const STEPS = [
  {
    fields: [
      'merchant.legalEntityType',
      'merchant.displayNameFa',
      'merchant.legalNameFa',
      'merchant.ownerNationalCode',
      'merchant.businessLicenseNumber',
      'merchant.nationalId',
      'merchant.registrationNumber',
    ],
    title: 'هویت',
  },
  {
    fields: [
      'merchant.primaryCategoryId',
      'merchant.contactMobileE164',
      'merchant.provinceId',
      'merchant.cityId',
      'merchant.addressFa',
    ],
    title: 'تماس و آدرس',
  },
  {
    fields: ['admin.firstNameFa', 'admin.lastNameFa', 'admin.mobileE164', 'branches'],
    title: 'مدیر و شعبه',
  },
  { description: 'اختیاری', fields: [], title: 'تنظیمات' },
  { fields: [], title: 'بازبینی' },
] as const;

/** `MerchantCreate` from the form: the other legal type's fields are sent as `null`. */
export function toMerchantCreate(values: WizardValues['merchant']): MerchantCreate {
  const individual = values.legalEntityType === 'INDIVIDUAL';
  return {
    addressFa: values.addressFa,
    businessLicenseNumber: individual ? values.businessLicenseNumber : null,
    cityId: values.cityId,
    contactMobileE164: values.contactMobileE164,
    displayNameFa: values.displayNameFa,
    legalEntityType: values.legalEntityType,
    legalNameFa: values.legalNameFa,
    nationalId: individual ? null : values.nationalId,
    ownerNationalCode: individual ? values.ownerNationalCode : null,
    primaryCategoryId: values.primaryCategoryId,
    provinceId: values.provinceId,
    registrationNumber: individual ? null : values.registrationNumber,
  };
}

type PartStatus = 'idle' | 'pending' | 'done' | 'error' | 'unknown';

interface Progress {
  admin: PartStatus;
  branches: Record<string, PartStatus>;
  error: unknown;
  merchant: PartStatus;
  merchantId: string | null;
  unmapped: FieldError[];
}

const IDENTITY_FIELDS: readonly string[] = STEPS[0].fields;
const MERCHANT_FIELDS: readonly string[] = [...STEPS[0].fields, ...STEPS[1].fields];

function stepOf(formPath: string): number {
  if (formPath.startsWith('admin.') || formPath.startsWith('branches')) return 2;
  return IDENTITY_FIELDS.includes(formPath) ? 0 : 1;
}

function splitLegalName(legalNameFa: string): { first: string; last: string } {
  const trimmed = legalNameFa.trim();
  const space = trimmed.indexOf(' ');
  if (space === -1) return { first: trimmed, last: '' };
  return { first: trimmed.slice(0, space), last: trimmed.slice(space + 1).trim() };
}

function IdentityStep({
  form,
  locked,
}: {
  readonly form: UseFormReturn<WizardInput, unknown, WizardValues>;
  readonly locked: boolean;
}) {
  const { control, formState, register, watch } = form;
  const errors = formState.errors.merchant;
  const type = watch('merchant.legalEntityType');
  return (
    <fieldset className="admin-fieldset" disabled={locked}>
      <legend>هویت و مدارک فروشگاه</legend>
      <Controller
        control={control}
        name="merchant.legalEntityType"
        render={({ field }) => (
          <SegmentedButtons
            label="نوع شخصیت"
            onChange={field.onChange}
            options={(['INDIVIDUAL', 'LEGAL_ENTITY'] as const).map((value) => ({
              key: value,
              label: statusLabel('merchantLegalEntityType', value),
            }))}
            value={field.value}
          />
        )}
      />
      <TextField
        error={errors?.displayNameFa?.message}
        hint="پس از اسکن QR به خریدار نمایش داده می‌شود."
        label="نام نمایشی فروشگاه"
        {...register('merchant.displayNameFa')}
      />
      <TextField
        error={errors?.legalNameFa?.message}
        label="نام حقوقی"
        {...register('merchant.legalNameFa')}
      />
      {type === 'INDIVIDUAL' ? (
        <div className="admin-grid-2">
          <Controller
            control={control}
            name="merchant.ownerNationalCode"
            render={({ field, fieldState }) => (
              <NationalCodeField
                error={fieldState.error?.message}
                label="کد ملی مالک"
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value}
              />
            )}
          />
          <TextField
            error={errors?.businessLicenseNumber?.message}
            label="شماره جواز کسب"
            ltr
            {...register('merchant.businessLicenseNumber')}
          />
        </div>
      ) : (
        <div className="admin-grid-2">
          <TextField
            error={errors?.nationalId?.message}
            hint="شناسه ملی ۱۱ رقمی شخص حقوقی."
            inputMode="numeric"
            label="شناسه ملی"
            ltr
            {...register('merchant.nationalId')}
          />
          <TextField
            error={errors?.registrationNumber?.message}
            label="شماره ثبت"
            ltr
            {...register('merchant.registrationNumber')}
          />
        </div>
      )}
    </fieldset>
  );
}

function ContactStep({
  form,
  locked,
}: {
  readonly form: UseFormReturn<WizardInput, unknown, WizardValues>;
  readonly locked: boolean;
}) {
  const { control, formState, register } = form;
  const errors = formState.errors.merchant;
  return (
    <fieldset className="admin-fieldset" disabled={locked}>
      <legend>دسته‌بندی، تماس و آدرس</legend>
      <Controller
        control={control}
        name="merchant.primaryCategoryId"
        render={({ field }) => (
          <CategoryPicker
            error={errors?.primaryCategoryId?.message}
            id="merchant-category"
            onChange={field.onChange}
            registration
            value={field.value}
          />
        )}
      />
      <Controller
        control={control}
        name="merchant.contactMobileE164"
        render={({ field, fieldState }) => (
          <MobileField
            error={fieldState.error?.message}
            label="شماره موبایل تماس"
            onBlur={field.onBlur}
            onChange={field.onChange}
            value={field.value}
          />
        )}
      />
      <Controller
        control={control}
        name="merchant.provinceId"
        render={({ field: province }) => (
          <Controller
            control={control}
            name="merchant.cityId"
            render={({ field: city }) => (
              <GeographyFields
                cityError={errors?.cityId?.message}
                cityId={city.value}
                idPrefix="merchant"
                onCityChange={city.onChange}
                onProvinceChange={province.onChange}
                provinceError={errors?.provinceId?.message}
                provinceId={province.value}
              />
            )}
          />
        )}
      />
      <TextArea
        error={errors?.addressFa?.message}
        label="آدرس"
        {...register('merchant.addressFa')}
      />
    </fieldset>
  );
}

function AdminStep({
  form,
  locked,
  ownerIsAdmin,
  setOwnerIsAdmin,
}: {
  readonly form: UseFormReturn<WizardInput, unknown, WizardValues>;
  readonly locked: boolean;
  readonly ownerIsAdmin: boolean;
  readonly setOwnerIsAdmin: (value: boolean) => void;
}) {
  const { formState, register } = form;
  const errors = formState.errors.admin;
  return (
    <fieldset className="admin-fieldset" disabled={locked}>
      <legend>اولین مدیر فروشگاه</legend>
      <Checkbox
        checked={ownerIsAdmin}
        disabled={locked}
        label="مالک، مدیر فروشگاه است"
        onChange={(event) => setOwnerIsAdmin(event.target.checked)}
      />
      <div className="admin-grid-2">
        <TextField
          error={errors?.firstNameFa?.message}
          label="نام"
          {...register('admin.firstNameFa')}
        />
        <TextField
          error={errors?.lastNameFa?.message}
          label="نام خانوادگی"
          {...register('admin.lastNameFa')}
        />
      </div>
      <TextField
        autoComplete="off"
        error={errors?.mobileE164?.message}
        inputMode="tel"
        label="شماره موبایل مدیر"
        ltr
        {...register('admin.mobileE164')}
      />
    </fieldset>
  );
}

function BranchesStep({
  branches,
  done,
  form,
}: {
  readonly branches: UseFieldArrayReturn<WizardInput, 'branches'>;
  readonly done: Readonly<Record<string, PartStatus>>;
  readonly form: UseFormReturn<WizardInput, unknown, WizardValues>;
}) {
  const { control, formState, getValues, register } = form;
  const { append, fields, remove } = branches;
  const errors = formState.errors.branches;
  return (
    <fieldset className="admin-fieldset">
      <legend>شعبه‌ها</legend>
      {(errors?.root?.message ?? errors?.message) ? (
        <p className="saay-field__error" role="alert">
          {errors?.root?.message ?? errors?.message}
        </p>
      ) : null}
      {fields.map((field, index) => {
        const branchErrors = errors?.[index];
        const created = done[field.id] === 'done';
        return (
          <fieldset
            className="admin-fieldset admin-fieldset--nested"
            disabled={created}
            key={field.id}
          >
            <legend>
              {`شعبه ${(index + 1).toLocaleString('fa-IR')}`}
              {created ? ' — ثبت شد' : ''}
            </legend>
            <TextField
              error={branchErrors?.nameFa?.message}
              label="نام شعبه"
              {...register(`branches.${index}.nameFa`)}
            />
            <Controller
              control={control}
              name={`branches.${index}.provinceId`}
              render={({ field: province }) => (
                <Controller
                  control={control}
                  name={`branches.${index}.cityId`}
                  render={({ field: city }) => (
                    <GeographyFields
                      cityError={branchErrors?.cityId?.message}
                      cityId={city.value}
                      idPrefix={`branch-${index}`}
                      onCityChange={city.onChange}
                      onProvinceChange={province.onChange}
                      provinceError={branchErrors?.provinceId?.message}
                      provinceId={province.value}
                    />
                  )}
                />
              )}
            />
            <TextArea
              error={branchErrors?.addressFa?.message}
              label="آدرس شعبه"
              {...register(`branches.${index}.addressFa`)}
            />
            <div className="admin-actions">
              <Button
                onClick={() => {
                  const merchant = getValues('merchant');
                  form.setValue(`branches.${index}.provinceId`, merchant.provinceId);
                  form.setValue(`branches.${index}.cityId`, merchant.cityId);
                  form.setValue(`branches.${index}.addressFa`, merchant.addressFa);
                }}
                size="sm"
                variant="ghost"
              >
                همان آدرس فروشگاه
              </Button>
              {fields.length > 1 && !created ? (
                <Button onClick={() => remove(index)} size="sm" variant="ghost">
                  حذف این شعبه
                </Button>
              ) : null}
            </div>
          </fieldset>
        );
      })}
      <Button onClick={() => append({ ...emptyBranch })} variant="secondary">
        افزودن شعبه دیگر
      </Button>
    </fieldset>
  );
}

/** What the wizard will save for one configuration item, or that it is left for later. */
function setupSummary(key: SetupKey, setup: SetupValues, holderName: string): ReactNode {
  if (!isSetupFilled(key, setup)) {
    return <span className="admin-muted">بعداً در صفحهٔ فروشگاه</span>;
  }
  switch (key) {
    case 'commercial':
      return formatRatePercent(setup.feeRate, { invalid: 'raw' });
    case 'plan':
      return [
        ...(setup.planType === ''
          ? []
          : [
              planLabel(
                setup.planType,
                setup.planType === 'NUMBERED' ? Number(setup.installmentCount) : null,
                setup.planType === 'NUMBERED' ? Number(setup.intervalDays) : null,
              ),
            ]),
        ...(setup.walletCash ? ['پرداخت نقدی'] : []),
      ].join('، ');
    case 'capacity':
      return setup.capacityRials === null ? null : <Money rials={setup.capacityRials} />;
    case 'payout':
      return (
        <>
          <Ltr>{normalizeShebaInput(setup.sheba)}</Ltr>
          {` — ${holderName}`}
        </>
      );
    default:
      return null;
  }
}

function Review({
  allowed,
  onEdit,
  setup,
  values,
}: {
  readonly allowed: ReadonlySet<SetupKey>;
  readonly onEdit: (step: number) => void;
  readonly setup: SetupValues;
  readonly values: WizardInput;
}) {
  const { admin, branches, merchant } = values;
  const identityId =
    merchant.legalEntityType === 'INDIVIDUAL'
      ? normalizeCodeInput(merchant.ownerNationalCode)
      : normalizeCodeInput(merchant.nationalId);
  return (
    <section aria-labelledby="review-title" className="saay-card">
      <h2 id="review-title">بازبینی پیش از ثبت</h2>
      <div className="admin-review-block">
        <div className="saay-sheet-section__head">
          <h3>هویت</h3>
          <Button onClick={() => onEdit(0)} size="sm" variant="ghost">
            ویرایش
          </Button>
        </div>
        <KeyValueList
          items={[
            { key: 'display', label: 'نام نمایشی', value: merchant.displayNameFa },
            { key: 'legal', label: 'نام حقوقی', value: merchant.legalNameFa },
            {
              key: 'type',
              label: 'نوع شخصیت',
              value: statusLabel('merchantLegalEntityType', merchant.legalEntityType),
            },
            {
              key: 'id',
              label: merchant.legalEntityType === 'INDIVIDUAL' ? 'کد ملی مالک' : 'شناسه ملی',
              value: <Ltr>{identityId}</Ltr>,
            },
          ]}
        />
      </div>
      <div className="admin-review-block">
        <div className="saay-sheet-section__head">
          <h3>تماس و آدرس</h3>
          <Button onClick={() => onEdit(1)} size="sm" variant="ghost">
            ویرایش
          </Button>
        </div>
        <KeyValueList
          items={[
            {
              key: 'mobile',
              label: 'شماره موبایل تماس',
              value: <Ltr>{normalizeMobileField(merchant.contactMobileE164)}</Ltr>,
            },
            { key: 'address', label: 'آدرس', value: merchant.addressFa },
          ]}
        />
      </div>
      <div className="admin-review-block">
        <div className="saay-sheet-section__head">
          <h3>مدیر و شعبه</h3>
          <Button onClick={() => onEdit(2)} size="sm" variant="ghost">
            ویرایش
          </Button>
        </div>
        <KeyValueList
          items={[
            {
              key: 'admin',
              label: 'مدیر فروشگاه',
              value: (
                <>
                  {`${admin.firstNameFa} ${admin.lastNameFa}`} —{' '}
                  <Ltr>{normalizeMobileField(admin.mobileE164)}</Ltr>
                </>
              ),
            },
            {
              key: 'branches',
              label: 'شعبه‌ها',
              value: branches.map((branch) => branch.nameFa).join('، '),
            },
          ]}
        />
      </div>
      <div className="admin-review-block">
        <div className="saay-sheet-section__head">
          <h3>تنظیمات</h3>
          <Button onClick={() => onEdit(3)} size="sm" variant="ghost">
            ویرایش
          </Button>
        </div>
        {allowed.size === 0 ? (
          <p className="admin-muted">{SETUP_NOT_ALLOWED}</p>
        ) : (
          <KeyValueList
            items={SETUP_ORDER.filter((key) => allowed.has(key)).map((key) => ({
              key,
              label: SETUP_LABELS[key],
              value: setupSummary(key, setup, merchant.legalNameFa),
            }))}
          />
        )}
      </div>
    </section>
  );
}

const SETUP_NOT_ALLOWED =
  'نرخ SAAY، طرح اقساطی، سقف ظرفیت و حساب تسویه را مدیر مالی یا مدیر ارشد پس از ثبت، در صفحهٔ فروشگاه ثبت می‌کند.';

/** The callers' configuration rights (AC §6.6): rate, capacity, and payout for Super and Finance. */
export function allowedSetup(self: AdminSelf): ReadonlySet<SetupKey> {
  return new Set<SetupKey>([
    ...(canUse(self, 'createCommercialSettingVersion') ? (['commercial'] as const) : []),
    ...(canPerform(self, 'replaceInstallmentPlanActivations') ? (['plan'] as const) : []),
    ...(canUse(self, 'requestCapacityAdjustment') ? (['capacity'] as const) : []),
    ...(canUse(self, 'requestPayoutAccount') ? (['payout'] as const) : []),
  ]);
}

/** One configuration item as a nested block; it is locked once sent, saved, or open. */
function SetupBlock({
  children,
  item,
  title,
}: {
  readonly children: ReactNode;
  readonly item: SetupItem;
  readonly title: string;
}) {
  const locked = isSetupLocked(item);
  const message = setupErrorMessage(item);
  return (
    <fieldset className="admin-fieldset admin-fieldset--nested" disabled={locked}>
      <legend>
        {title}
        {isSetupSettled(item) ? ` — ${SETUP_PART_LABELS[item.status]}` : ''}
      </legend>
      {children}
      {message === undefined ? null : (
        <p className="saay-field__error" role="alert">
          {message}
        </p>
      )}
    </fieldset>
  );
}

/**
 * «تنظیمات» (optional): what is entered here is saved with the merchant, so the merchant page does
 * not ask for it again. The plan carries its count and interval (D-79), checked against the platform
 * `installment-bounds` policy; there is no merchant-specific range to fill.
 */
function SetupStep({
  allowed,
  bounds,
  errors,
  legalNameFa,
  progress,
  setValues,
  values,
}: {
  readonly allowed: ReadonlySet<SetupKey>;
  readonly bounds: PlanIntervalBounds | undefined;
  readonly errors: SetupFieldErrors;
  readonly legalNameFa: string;
  readonly progress: SetupProgress;
  readonly setValues: (patch: Partial<SetupValues>) => void;
  readonly values: SetupValues;
}) {
  if (allowed.size === 0) {
    return (
      <fieldset className="admin-fieldset">
        <legend>تنظیمات تجاری (اختیاری)</legend>
        <p className="admin-muted">{SETUP_NOT_ALLOWED}</p>
      </fieldset>
    );
  }
  return (
    <fieldset className="admin-fieldset">
      <legend>تنظیمات تجاری (اختیاری)</legend>
      <p className="admin-muted">
        هر موردی که اینجا وارد کنید همراه فروشگاه ثبت می‌شود و در صفحهٔ فروشگاه دوباره پرسیده
        نمی‌شود. موردی را که خالی بگذارید، بعداً در صفحهٔ فروشگاه ثبت کنید.
      </p>
      {allowed.has('commercial') ? (
        <SetupBlock item={progress.commercial} title={SETUP_LABELS.commercial}>
          <PercentField
            label="نرخ SAAY"
            onChangeRate={(rate) => setValues({ feeRate: rate ?? '' })}
            valueRate={values.feeRate === '' ? null : values.feeRate}
          />
        </SetupBlock>
      ) : null}
      {allowed.has('plan') ? (
        <SetupBlock item={progress.plan} title={SETUP_LABELS.plan}>
          <PlanChoiceField
            count={values.installmentCount}
            error={errors.planType ?? errors.installmentCount}
            onChange={(type, count) =>
              setValues({ installmentCount: count, planType: type as SetupValues['planType'] })
            }
            optional={values.walletCash}
            type={values.planType}
          />
          <Checkbox
            checked={values.walletCash}
            label="پرداخت نقدی از کیف پول"
            onChange={(event) => setValues({ walletCash: event.target.checked })}
          />
          {values.planType === 'NUMBERED' ? (
            <PlanIntervalField
              bounds={bounds}
              error={errors.intervalDays}
              id="merchant-setup-plan-interval"
              onChange={(intervalDays) => setValues({ intervalDays })}
              value={values.intervalDays}
            />
          ) : null}
          {values.planType === '' ? null : (
            <div className="admin-actions">
              <Button
                onClick={() => setValues({ installmentCount: '', planType: '' })}
                size="sm"
                variant="ghost"
              >
                بعداً تعیین می‌کنم
              </Button>
            </div>
          )}
        </SetupBlock>
      ) : null}
      {allowed.has('capacity') ? (
        <SetupBlock item={progress.capacity} title={SETUP_LABELS.capacity}>
          <MoneyField
            label="سقف ظرفیت فروش"
            onChangeRials={(rials) => setValues({ capacityRials: rials })}
            valueRials={values.capacityRials}
          />
        </SetupBlock>
      ) : null}
      {allowed.has('payout') ? (
        <SetupBlock item={progress.payout} title={SETUP_LABELS.payout}>
          <ShebaField
            error={errors.sheba}
            label="شماره شبا"
            onChange={(sheba) => setValues({ sheba })}
            value={values.sheba}
          />
          <p className="admin-muted">
            {`صاحب حساب: ${legalNameFa === '' ? 'نام حقوقی فروشگاه' : legalNameFa}. مالکیت حساب پس از ثبت، در بخش «حساب تسویه» بررسی و تأیید می‌شود.`}
          </p>
        </SetupBlock>
      ) : null}
    </fieldset>
  );
}

const PART_LABELS: Record<PartStatus, string> = {
  done: 'ثبت شد',
  error: 'ثبت نشد',
  idle: 'در انتظار',
  pending: 'در حال ثبت…',
  unknown: 'نتیجه نامشخص',
};

const SETUP_PART_LABELS: Record<SetupItem['status'], string> = {
  ...PART_LABELS,
  approval: 'برای تأیید ارسال شد',
};

function ProgressList({
  branchNames,
  progress,
  setup,
  setupKeysShown,
}: {
  readonly branchNames: readonly [string, string][];
  readonly progress: Progress;
  readonly setup: SetupProgress;
  readonly setupKeysShown: readonly SetupKey[];
}) {
  return (
    <ul aria-label="وضعیت ثبت" aria-live="polite" className="admin-progress">
      <li data-status={progress.merchant}>فروشگاه: {PART_LABELS[progress.merchant]}</li>
      {branchNames.map(([id, name]) => (
        <li data-status={progress.branches[id] ?? 'idle'} key={id}>
          {`شعبه «${name}»: ${PART_LABELS[progress.branches[id] ?? 'idle']}`}
        </li>
      ))}
      <li data-status={progress.admin}>مدیر فروشگاه: {PART_LABELS[progress.admin]}</li>
      {setupKeysShown.map((key) => {
        const item = setup[key];
        const message = item.status === 'error' ? setupErrorMessage(item) : undefined;
        return (
          <li data-status={item.status} key={key}>
            {`${SETUP_LABELS[key]}: ${SETUP_PART_LABELS[item.status]}${message === undefined ? '' : ` — ${message}`}`}
          </li>
        );
      })}
    </ul>
  );
}

function MerchantWizard({
  onCancel,
  self,
}: {
  readonly onCancel: () => void;
  readonly self: AdminSelf;
}) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const offline = useSubmitBlocked();
  const [step, setStep] = useState(0);
  const [ownerIsAdmin, setOwnerIsAdmin] = useState(false);
  const allowed = allowedSetup(self);
  const [setupValues, setSetupValues] = useState<SetupValues>(EMPTY_SETUP);
  const [setupErrors, setSetupErrors] = useState<SetupFieldErrors>({});
  const [setup, setSetup] = useState<SetupProgress>(IDLE_SETUP);
  const [marketingDraft, setMarketingDraft] = useState<MarketingSetupDraft>(emptyMarketingSetup);
  const [marketingError, setMarketingError] = useState<string | undefined>(undefined);
  const marketingAllowed = self.roles.includes('SUPER_ADMIN');
  // The platform bounds a NUMBERED plan must fit (AC §17.3.3 rule 3, §23.3): a new merchant has no
  // merchant bounds, so they are its effective bounds. The server checks them again.
  const boundsPolicy = useCurrentPolicy<InstallmentBoundsPolicyVersion>(
    'installment-bounds',
    allowed.has('plan'),
  );
  const bounds: PlanIntervalBounds | undefined =
    boundsPolicy.data === undefined
      ? undefined
      : {
          maxIntervalDays: boundsPolicy.data.maxIntervalDays,
          maxTotalDurationDays: boundsPolicy.data.maxTotalDurationDays,
          minIntervalDays: boundsPolicy.data.minIntervalDays,
        };
  const form = useForm<WizardInput, unknown, WizardValues>({
    defaultValues: defaults,
    mode: 'onTouched',
    resolver: zodResolver(merchantWizardSchema),
  });
  const branches = useFieldArray({ control: form.control, name: 'branches' });
  const branchFields = branches.fields;
  const intents = useRef({
    admin: createIntent('merchant.admin.create'),
    branches: new Map<string, CommandIntent>(),
    merchant: createIntent('merchant.create'),
    setup: {} as Partial<Record<SetupKey, CommandIntent>>,
  });
  const [progress, setProgress] = useState<Progress>({
    admin: 'idle',
    branches: {},
    error: undefined,
    merchant: 'idle',
    merchantId: null,
    unmapped: [],
  });
  const running =
    progress.merchant === 'pending' ||
    progress.admin === 'pending' ||
    Object.values(progress.branches).includes('pending') ||
    SETUP_ORDER.some((key) => setup[key].status === 'pending');
  const locked = progress.merchantId !== null;

  const legalName = form.watch('merchant.legalNameFa');
  const contactMobile = form.watch('merchant.contactMobileE164');

  useEffect(() => {
    if (!ownerIsAdmin) return;
    const { first, last } = splitLegalName(legalName);
    if (first !== '') form.setValue('admin.firstNameFa', first);
    if (last !== '') form.setValue('admin.lastNameFa', last);
    if (contactMobile !== '') form.setValue('admin.mobileE164', contactMobile);
  }, [contactMobile, form, legalName, ownerIsAdmin]);

  const patchSetup = (patch: Partial<SetupValues>) => {
    setSetupValues((current) => ({ ...current, ...patch }));
    setSetupErrors({});
  };

  function marketingValid(): boolean {
    if (!marketingAllowed) return true;
    const error = marketingSetupError(marketingDraft);
    setMarketingError(error);
    if (error !== undefined) {
      setStep(3);
      return false;
    }
    return true;
  }

  /** Checks «تنظیمات»; on a problem it shows the errors and opens the step. */
  function setupValid(): boolean {
    const errors = validateSetup(setupValues, allowed, bounds, tehranToday());
    setSetupErrors(errors);
    if (hasSetupErrors(errors)) {
      setStep(3);
      return false;
    }
    return true;
  }

  async function next() {
    const fields = STEPS[step]!.fields as readonly string[];
    const valid =
      fields.length === 0 || (await form.trigger(fields as never, { shouldFocus: true }));
    if (!valid || (step === 3 && (!setupValid() || !marketingValid()))) return;
    if (step === 1) {
      const merchant = form.getValues('merchant');
      form.setValue('branches.0.provinceId', merchant.provinceId);
      form.setValue('branches.0.cityId', merchant.cityId);
      form.setValue('branches.0.addressFa', merchant.addressFa);
    }
    setStep((current) => Math.min(current + 1, STEPS.length - 1));
  }

  /** Shows a failed part's error on its fields and jumps to the first affected step. */
  function fail(error: unknown, prefix: string, fields: readonly string[], fallbackStep: number) {
    if (error instanceof UnknownResultError || (isApiError(error) && error.retryable)) {
      // Nothing to correct: stay on the review step, where «بررسی وضعیت و ادامه» resends.
      return [];
    }
    const names = fields.map((field) => `${prefix}${field}`);
    const unmapped = applyFieldErrors(error, names, form.setError, prefix);
    let target = fallbackStep;
    const first = isApiError(error) ? error.fieldErrors[0] : undefined;
    if (first !== undefined) {
      target = stepOf(`${prefix}${toFormPath(first.field)}`);
    }
    if (isApiError(error) && error.code === 'GEOGRAPHY_MISMATCH') {
      form.setError(`${prefix}cityId` as never, { message: error.messageFa, type: 'server' });
      target = stepOf(`${prefix}cityId`);
    }
    setStep(target);
    return unmapped;
  }

  /** The configuration items that were wanted but are not saved, for the merchant page banner. */
  function notSaved(final: SetupProgress): MerchantCreatedState['setupNotSaved'] {
    return wantedSetup(setupValues, allowed)
      .filter((key) => !isSetupSettled(final[key]))
      .map((key) => ({
        key,
        label: SETUP_LABELS[key],
        messageFa: setupErrorMessage(final[key]) ?? 'ثبت نشد.',
      }));
  }

  /** Leaves for the merchant page; anything not saved is listed there in a banner. */
  function openMerchant(merchantId: string, final: SetupProgress) {
    const missing = notSaved(final);
    void navigate(`/merchants/${merchantId}`, {
      replace: true,
      ...(missing.length === 0
        ? {}
        : { state: { setupNotSaved: missing } satisfies MerchantCreatedState }),
    });
  }

  async function submit(values: WizardValues) {
    if (!setupValid() || !marketingValid()) return;
    let merchantId = progress.merchantId;
    let lastResult: ApiResult<unknown> | undefined;
    const update = (patch: Partial<Progress>) =>
      setProgress((current) => ({ ...current, ...patch }));
    const partFailed = (error: unknown) =>
      error instanceof UnknownResultError ? 'unknown' : 'error';
    const finish = (intent: CommandIntent, error: unknown) => {
      if (!(error instanceof UnknownResultError) && !(isApiError(error) && error.retryable)) {
        intent.complete();
      }
    };

    update({ error: undefined, unmapped: [] });

    if (merchantId === null) {
      update({ merchant: 'pending' });
      try {
        const marketingSetup = marketingAllowed
          ? toMerchantMarketingSetup(marketingDraft)
          : undefined;
        const result = await client.post<AdminMerchant>('/admin/merchants', {
          body: {
            ...toMerchantCreate(values.merchant),
            ...(marketingSetup === undefined ? {} : { marketingSetup }),
          },
          intent: intents.current.merchant,
        });
        intents.current.merchant.complete();
        merchantId = result.data.id;
        lastResult = result;
        update({ merchant: 'done', merchantId });
      } catch (error) {
        finish(intents.current.merchant, error);
        const unmapped = fail(
          error,
          'merchant.',
          MERCHANT_FIELDS.map((field) => field.slice('merchant.'.length)),
          0,
        );
        update({ error, merchant: partFailed(error), unmapped });
        return;
      }
    }

    const branchState: Record<string, PartStatus> = { ...progress.branches };
    for (const [index, field] of branchFields.entries()) {
      if (branchState[field.id] === 'done') continue;
      const branch = values.branches[index]!;
      let intent = intents.current.branches.get(field.id);
      if (intent === undefined) {
        intent = createIntent('merchant.branch.create');
        intents.current.branches.set(field.id, intent);
      }
      branchState[field.id] = 'pending';
      update({ branches: { ...branchState } });
      try {
        const body: MerchantBranchCreate = branch;
        lastResult = await client.post<MerchantBranch>(`/admin/merchants/${merchantId}/branches`, {
          body,
          intent,
        });
        intent.complete();
        branchState[field.id] = 'done';
        update({ branches: { ...branchState } });
      } catch (error) {
        finish(intent, error);
        branchState[field.id] = partFailed(error);
        const unmapped = fail(
          error,
          `branches.${index}.`,
          ['nameFa', 'provinceId', 'cityId', 'addressFa'],
          2,
        );
        update({ branches: { ...branchState }, error, unmapped });
        return;
      }
    }

    if (progress.admin !== 'done') {
      update({ admin: 'pending' });
      try {
        const body: MerchantUserCreate = {
          branchIds: [],
          firstNameFa: values.admin.firstNameFa,
          lastNameFa: values.admin.lastNameFa,
          mobileE164: values.admin.mobileE164,
          role: 'MERCHANT_ADMIN',
        };
        lastResult = await client.post<MerchantUserCreated>(
          `/admin/merchants/${merchantId}/users`,
          {
            body,
            intent: intents.current.admin,
          },
        );
        intents.current.admin.complete();
        update({ admin: 'done' });
      } catch (error) {
        finish(intents.current.admin, error);
        const unmapped = fail(error, 'admin.', ['firstNameFa', 'lastNameFa', 'mobileE164'], 2);
        update({ admin: partFailed(error), error, unmapped });
        return;
      }
    }

    // «تنظیمات»: each item with the merchant's current version; nothing is swallowed (owner fix).
    const wanted = wantedSetup(setupValues, allowed);
    const final = await runSetup({
      client,
      holderName: values.merchant.legalNameFa,
      intents: intents.current.setup,
      merchantId,
      onItem: (key, item) => setSetup((current) => ({ ...current, [key]: item })),
      progress: setup,
      today: tehranToday(),
      values: setupValues,
      wanted,
    });

    await queryClient.invalidateQueries({ queryKey: setupKeys.merchants });
    const unsaved = wanted.filter((key) => !isSetupSettled(final[key]));
    if (unsaved.length > 0) {
      // Stay on the review step: the list says which item failed and why, and the admin retries
      // it here or leaves for the merchant page.
      setStep(4);
      return;
    }
    // Success only on a server answer: the last create command, else the last saved item.
    const confirmedBy =
      lastResult ??
      [...wanted]
        .reverse()
        .map((key) => final[key].confirmedBy)
        .find((result) => result !== undefined);
    if (confirmedBy !== undefined) {
      toast.success({
        confirmedBy,
        description:
          wanted.length === 0
            ? 'فعال‌سازی پس از تکمیل کارمزد، ظرفیت، طرح و حساب تسویه انجام می‌شود.'
            : 'فهرست آمادگی فعال‌سازی را در صفحهٔ فروشگاه ببینید.',
        title:
          wanted.length === 0 ? 'فروشگاه، شعبه‌ها و مدیر آن ثبت شد' : 'فروشگاه و تنظیمات آن ثبت شد',
      });
    }
    openMerchant(merchantId, final);
  }

  const values = form.watch();
  const unknownOrError = progress.error !== undefined;
  const setupShown = wantedSetup(setupValues, allowed).filter(
    (key) => setup[key].status !== 'idle',
  );
  const setupOpen = SETUP_ORDER.some(
    (key) =>
      setup[key].status === 'unknown' ||
      (isSetupLocked(setup[key]) && setup[key].status === 'error'),
  );
  const setupFailed = setupShown.some((key) => !isSetupSettled(setup[key]));
  const peopleDone = locked && progress.admin === 'done';

  return (
    <>
      <WizardStepper current={step} label="مراحل ثبت فروشگاه" onSelect={setStep} steps={STEPS} />
      {locked ? (
        <Banner tone="info">
          فروشگاه ثبت شده است؛ مشخصات آن در این مرحله ویرایش نمی‌شود. بخش‌های باقی‌مانده را تکمیل
          کنید.
        </Banner>
      ) : null}
      <form
        aria-label="ثبت فروشگاه"
        className="admin-form admin-wizard"
        noValidate
        onSubmit={form.handleSubmit(submit, (errors) => {
          const firstPath = Object.keys(errors)[0];
          if (firstPath === 'merchant') {
            const field = Object.keys(errors.merchant ?? {})[0] ?? '';
            setStep(stepOf(`merchant.${field}`));
          } else if (firstPath === 'admin' || firstPath === 'branches') setStep(2);
        })}
      >
        <div hidden={step !== 0}>
          <IdentityStep form={form} locked={locked} />
        </div>
        <div hidden={step !== 1}>
          <ContactStep form={form} locked={locked} />
        </div>
        <div hidden={step !== 2}>
          <AdminStep
            form={form}
            locked={progress.admin === 'done'}
            ownerIsAdmin={ownerIsAdmin}
            setOwnerIsAdmin={setOwnerIsAdmin}
          />
          <BranchesStep branches={branches} done={progress.branches} form={form} />
        </div>
        <div hidden={step !== 3}>
          <SetupStep
            allowed={allowed}
            bounds={bounds}
            errors={setupErrors}
            legalNameFa={values.merchant.legalNameFa}
            progress={setup}
            setValues={patchSetup}
            values={setupValues}
          />
          {marketingAllowed ? (
            <MerchantMarketingSetupFields
              draft={marketingDraft}
              {...(marketingError === undefined ? {} : { error: marketingError })}
              onChange={(next) => {
                setMarketingDraft(next);
                setMarketingError(undefined);
              }}
            />
          ) : null}
        </div>
        {step === 4 ? (
          <Review allowed={allowed} onEdit={setStep} setup={setupValues} values={values} />
        ) : null}

        {progress.merchant !== 'idle' ? (
          <ProgressList
            branchNames={branchFields.map((field, index) => [
              field.id,
              values.branches[index]?.nameFa ?? '',
            ])}
            progress={progress}
            setup={setup}
            setupKeysShown={setupShown}
          />
        ) : null}
        <FieldErrorList errors={progress.unmapped} />
        {unknownOrError ? (
          progress.error instanceof UnknownResultError ? (
            <Banner tone="warning">
              نتیجه آخرین درخواست مشخص نیست. «بررسی وضعیت و ادامه» همان درخواست را با همان کلید
              دوباره می‌فرستد و چیزی دوبار ثبت نمی‌شود.
            </Banner>
          ) : isApiError(progress.error) && progress.error.fieldErrors.length > 0 ? null : (
            <ErrorState error={progress.error} title="ثبت انجام نشد" />
          )
        ) : null}
        {peopleDone && setupFailed && !running ? (
          <Banner tone="warning">
            {setupOpen
              ? 'نتیجهٔ ثبت بخشی از تنظیمات مشخص نیست. «بررسی وضعیت و ادامه» همان درخواست را با همان کلید دوباره می‌فرستد و چیزی دوبار ثبت نمی‌شود.'
              : 'فروشگاه ثبت شد، اما بخشی از تنظیمات ثبت نشد (فهرست بالا). مقدار را در «تنظیمات» اصلاح و دوباره ثبت کنید، یا به صفحهٔ فروشگاه بروید و آن را همان‌جا ثبت کنید.'}
          </Banner>
        ) : null}
        {isApiError(progress.error) &&
        progress.error.code === 'MERCHANT_NATIONAL_ID_ALREADY_REGISTERED' &&
        typeof progress.error.details?.merchantId === 'string' ? (
          <p>
            <Link to={`/merchants/${progress.error.details.merchantId}`}>
              مشاهده فروشگاه ثبت‌شده
            </Link>
          </p>
        ) : null}

        <div className="admin-actions admin-actions--end">
          {step > 0 ? (
            <Button disabled={running} onClick={() => setStep(step - 1)} variant="secondary">
              مرحله قبل
            </Button>
          ) : (
            <Button onClick={onCancel} variant="secondary">
              انصراف
            </Button>
          )}
          {peopleDone && setupFailed && !running && progress.merchantId !== null ? (
            <Button
              key="leave"
              onClick={() => openMerchant(progress.merchantId!, setup)}
              variant="secondary"
            >
              رفتن به صفحهٔ فروشگاه
            </Button>
          ) : null}
          {/* Distinct keys: reusing one <button> would turn «مرحله بعد» into the submit button
              during its own click, and the browser would submit the form. */}
          {step < STEPS.length - 1 ? (
            <Button key="next" onClick={() => void next()}>
              مرحله بعد
            </Button>
          ) : (
            <Button disabled={offline.blocked} key="submit" loading={running} type="submit">
              {progress.error instanceof UnknownResultError || setupOpen
                ? 'بررسی وضعیت و ادامه'
                : peopleDone && setupFailed
                  ? 'ثبت دوبارهٔ موارد ثبت‌نشده'
                  : locked
                    ? 'ادامه ثبت'
                    : 'ثبت فروشگاه'}
            </Button>
          )}
        </div>
        {offline.reason === undefined ? null : <p className="admin-muted">{offline.reason}</p>}
      </form>
    </>
  );
}

/**
 * `/merchants/new`: the prototype registration sheet (`merchantSheet`, the wide registration drawer)
 * over the merchants list. A direct load renders the list underneath; closing returns to it.
 */
export function MerchantCreateSheet() {
  const { self } = useOutletContext<MerchantsOutletContext>();
  const close = useCloseSheet('/merchants');
  usePageTitle('افزودن فروشگاه');
  return (
    <Dialog
      eyebrow="فروشگاه جدید"
      onOpenChange={onSheetOpenChange(close)}
      open
      size="wizard"
      stickyFooter
      title="افزودن فروشگاه"
    >
      {canPerform(self, 'createMerchant') ? (
        <MerchantWizard onCancel={close} self={self} />
      ) : (
        <section className="saay-panel" role="alert">
          <h3 className="saay-panel__title">به این بخش دسترسی ندارید</h3>
          <p className="saay-panel__text">نقش شما اجازه ثبت فروشگاه را ندارد.</p>
        </section>
      )}
    </Dialog>
  );
}
